import { useState } from "react";
import { api } from "../lib/api.js";
import { dateTime } from "../lib/format.js";
import { Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

interface Session {
  id: string; username?: string; ip: string; created_at: number;
  expires_at: number; current: boolean; device_info: Record<string, string>;
}

export default function Account({ me, onToggleRail }: PageProps) {
  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Your account</h1>
          <span className="topbar__hint">@{me.name}</span>
        </div>
      </header>
      <div className="content">
        <div className="stack" style={{ maxWidth: 640 }}>
          <TwoFactor enabled={me.tfa_enabled} />
          <ChangePassword />
          <Sessions />
        </div>
      </div>
    </>
  );
}

function TwoFactor({ enabled }: { enabled: boolean }) {
  const [setup, setSetup] = useState<{ secret: string; url: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [on, setOn] = useState(enabled);

  async function start() {
    setError("");
    try { setSetup(await api.post<{ secret: string; url: string }>("/api/console/me/2fa/setup")); }
    catch (e) { setError((e as Error).message); }
  }

  async function confirm() {
    setError("");
    try { await api.post("/api/console/me/2fa/enable", { code }); setOn(true); setSetup(null); setCode(""); }
    catch (e) { setError((e as Error).message); }
  }

  async function disable() {
    setError("");
    try { await api.post("/api/console/me/2fa/disable", { code }); setOn(false); setCode(""); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="panel">
      <div className="panel__head"><h2>Two-factor authentication</h2></div>
      <div className="panel__body">
        {error ? <div style={{ marginBottom: "0.75rem" }}><Notice>{error}</Notice></div> : null}

        {on ? (
          <>
            <p>
              <span className="tag tag--live">On</span> You are asked for a code from your
              authenticator app each time you sign in.
            </p>
            <div className="row">
              <input className="mono" style={{ maxWidth: 160 }} value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="000000" inputMode="numeric" />
              <button className="btn btn--danger" onClick={disable} disabled={code.length < 6}>
                Turn off
              </button>
            </div>
          </>
        ) : setup ? (
          <>
            <p>Scan this in your authenticator app, then enter the code it shows.</p>
            <div className="field">
              <label className="field__label">Setup key</label>
              <code className="mono" style={{ wordBreak: "break-all" }}>{setup.secret}</code>
            </div>
            <div className="field">
              <label className="field__label">Setup URL</label>
              <code className="mono small" style={{ wordBreak: "break-all" }}>{setup.url}</code>
            </div>
            <div className="row">
              <input className="mono" style={{ maxWidth: 160 }} value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="000000" inputMode="numeric" autoFocus />
              <button className="btn btn--primary" onClick={confirm} disabled={code.length < 6}>
                Turn on
              </button>
              <button className="btn btn--ghost" onClick={() => setSetup(null)}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <p className="faint">
              Add a second step at sign-in using an authenticator app.
            </p>
            <button className="btn btn--primary" onClick={start}>Set up two-factor</button>
          </>
        )}
      </div>
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function save() {
    setError(""); setDone(false);
    try {
      await api.post("/api/console/me/password", { current, new: next });
      setDone(true); setCurrent(""); setNext("");
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="panel">
      <div className="panel__head"><h2>Password</h2></div>
      <div className="panel__body">
        {error ? <div style={{ marginBottom: "0.75rem" }}><Notice>{error}</Notice></div> : null}
        {done ? (
          <div style={{ marginBottom: "0.75rem" }}>
            <Notice kind="ok">Password changed. Other sessions were signed out.</Notice>
          </div>
        ) : null}
        <div className="field">
          <label className="field__label">Current password</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password" />
        </div>
        <div className="field">
          <label className="field__label">New password</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password" />
          <span className="field__hint">At least 8 characters.</span>
        </div>
        <button className="btn btn--primary" onClick={save} disabled={!current || next.length < 8}>
          Change password
        </button>
      </div>
    </div>
  );
}

function Sessions() {
  const sessions = useAsync(() => api.get<Session[]>("/api/console/sessions"), []);
  return (
    <div className="panel">
      <div className="panel__head">
        <h2>Signed-in devices</h2>
        <button className="btn btn--sm" onClick={sessions.reload}>Refresh</button>
      </div>
      <div className="panel__body" style={{ padding: 0 }}>
        <table className="data">
          <thead><tr><th>Device</th><th>From</th><th>Signed in</th><th style={{ width: 90 }} /></tr></thead>
          <tbody>
            {(sessions.data ?? []).map((s) => (
              <tr key={s.id}>
                <td>
                  {s.device_info?.name || s.device_info?.os || "Unknown device"}
                  {s.current ? <span className="tag tag--live" style={{ marginLeft: 8 }}>This one</span> : null}
                </td>
                <td className="mono small faint">{s.ip}</td>
                <td className="small faint">{dateTime(s.created_at)}</td>
                <td className="actions">
                  {!s.current ? (
                    <button className="btn btn--danger btn--sm"
                      onClick={() => api.del(`/api/console/sessions/${s.id}`).then(sessions.reload)}>
                      Sign out
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
